import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Link,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { AuthService } from '../../services/auth.service';
import bgImage from '../../assets/images/bg-app-4.jpg';
import tacticaLogo from '../../assets/images/tacticaLogov3.png';
import { useTranslation } from '../../i18n';
import { LangContext } from '../../landing';
import { MAIN_APP_URL } from '../../config/firebase.config';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { lang } = useContext(LangContext);
  const { t } = useTranslation(lang);

  // Charger les identifiants mémorisés au montage du composant
  useEffect(() => {
    const savedEmail = localStorage.getItem('tacticafoot_email');
    const savedRememberMe = localStorage.getItem('tacticafoot_remember') === 'true';
    
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Éviter les problèmes de page blanche
  useEffect(() => {
    // Forcer la couleur de fond sur le body et html
    document.body.style.backgroundColor = '#1a1a1a';
    document.documentElement.style.backgroundColor = '#1a1a1a';
    
    // Ajouter un style global pour masquer les anciens fonds fixes problématiques (mais laisser le nouveau fond vidéo)
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      div[style*="background: linear-gradient"][style*="position: fixed"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      // Nettoyer lors du démontage
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleOpenResetDialog = () => {
    setResetEmail(email); // Pré-remplir avec l'email saisi
    setResetDialogOpen(true);
    setResetSuccess(false);
    setResetError('');
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetEmail('');
    setResetSuccess(false);
    setResetError('');
  };

  const handleResetPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      setResetError(t('login.errors.invalidEmail'));
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      await AuthService.resetPassword(resetEmail);
      setResetSuccess(true);
      // Fermer automatiquement après 3 secondes
      setTimeout(() => {
        handleCloseResetDialog();
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      setResetError(t('login.resetPassword.error'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔐 Tentative de connexion...');
      const user = await signIn(email, password);
      console.log('✅ Connexion réussie, données utilisateur:', { 
        id: user.id, 
        email: user.email, 
        subscriptionStatus: user.subscriptionStatus,
        role: user.role 
      });

      // Gérer la mémorisation des identifiants
      if (rememberMe) {
        localStorage.setItem('tacticafoot_email', email);
        localStorage.setItem('tacticafoot_remember', 'true');
      } else {
        localStorage.removeItem('tacticafoot_email');
        localStorage.removeItem('tacticafoot_remember');
      }

      if (user.subscriptionStatus === 'active' || user.role === 'admin') {
        console.log('🚀 Redirection vers l\'application principale...');
        // Redirection immédiate sans délai artificiel
        window.location.href = `${MAIN_APP_URL}/app`;
      } else {
        console.log('📚 Redirection vers le tutoriel...');
        // Rediriger les utilisateurs sans abonnement actif vers la page tutoriel
        navigate('/tutorial');
      }
    } catch (err) {
      console.error('❌ Erreur lors de la connexion:', err);
      
      // Gestion améliorée des erreurs
      let errorMessage = t('login.errors.default');
      
      if (err instanceof Error) {
        const errorCode = (err as any).code || err.message;
        console.log('Code d\'erreur:', errorCode);
        
        switch (errorCode) {
          case 'auth/invalid-email':
            errorMessage = t('login.errors.invalidEmail');
            break;
          case 'auth/user-disabled':
            errorMessage = t('login.errors.userDisabled');
            break;
          case 'auth/user-not-found':
            errorMessage = t('login.errors.userNotFound');
            break;
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            errorMessage = t('login.errors.wrongPassword');
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Trop de tentatives de connexion. Veuillez réessayer plus tard.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Erreur réseau. Vérifiez votre connexion internet.';
            break;
          default:
            errorMessage = `${t('login.errors.default')} (${errorCode})`;
        }
      } else {
        // Erreur non-standard
        errorMessage = 'Une erreur inattendue s\'est produite. Veuillez réessayer.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      bgcolor: '#1a1a1a'
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mt: 8 }}>
        <Link component={RouterLink} to="/" sx={{ display: 'block', textAlign: 'center' }}>
          <Box 
            component="img"
            src={tacticaLogo}
            alt="Tactica Logo"
            sx={{
              width: '180px',
              height: 'auto',
              mb: '20px',
              display: 'block',
              margin: '0 auto'
            }}
          />
        </Link>

        <Typography component="h1" variant="h5" align="center" gutterBottom>
          {t('login.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('login.fields.email')}
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Box sx={{ position: 'relative' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.fields.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <IconButton
              aria-label={showPassword ? t('login.fields.hidePassword') : t('login.fields.showPassword')}
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
              />
            }
            label={t('login.fields.rememberMe')}
            sx={{ mt: 1, mb: 1 }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t('login.buttons.login')}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Link 
              component="button"
              type="button"
              variant="body2" 
              onClick={handleOpenResetDialog}
              sx={{ cursor: 'pointer' }}
            >
              {t('login.buttons.forgotPassword')}
            </Link>
            <Link 
              component={RouterLink} 
              to="/signup" 
              variant="body2"
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('login.buttons.signup')}
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Modal de réinitialisation du mot de passe */}
      {resetDialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
          }}
          onClick={handleCloseResetDialog}
        >
          <Paper
            sx={{
              width: { xs: '90%', sm: 500 },
              maxWidth: '90vw',
              p: 4,
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              {t('login.resetPassword.title')}
            </Typography>
            
            {resetSuccess ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t('login.resetPassword.success')}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
                  {t('login.resetPassword.description')}
                </Typography>
                {resetError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {resetError}
                  </Alert>
                )}
                <TextField
                  autoFocus
                  margin="normal"
                  label={t('login.fields.email')}
                  type="email"
                  fullWidth
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={resetLoading}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                  <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
                    {t('login.resetPassword.cancel')}
                  </Button>
                  <Button 
                    onClick={handleResetPassword} 
                    variant="contained" 
                    disabled={resetLoading}
                  >
                    {resetLoading ? <CircularProgress size={24} /> : t('login.resetPassword.button')}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};
